/*!
 * KoliBri - The accessible HTML-Standard
 */
import { e as watchBoolean, g as watchString, p as parseJson, o as objectObjectHandler, b as watchValidator, H as isObject, i as isString, s as setState, k as a11yHint } from './common-D-vggmh2.js';
import { A as AssociatedInputController, v as validateAccessKey, a as validateShortKey, b as validateTabIndex } from './associated.controller-FCQDejnI.js';
import { v as validateDisabled } from './disabled-CL52u2mm.js';
import { v as validateHideLabel } from './hide-label-CRGm6KGP.js';
import { a as validateLabelWithExpertSlot } from './label-w3T7Y2ih.js';
import { v as validateTooltipAlign } from './tooltip-align-BQmLWbBy.js';
import { v as validateVariantClassName, c as classNameFromVariant } from './variant-class-name-9qZ5egaq.js';
import { d as dispatchDomEvent, K as KolEvent } from './events-BhfZTW2e.js';
import { v as validateAccessAndShortKey } from './access-and-short-key-ijzCZfHm.js';
import { a as __rest } from './tslib.es6-QNbPBOk5.js';
import { h } from './index-C_ZKddLG.js';
import { t as translate } from './i18n-CvFPh0g5.js';
import { B as BaseWebComponent } from './base-web-component-D909Fl-Y.js';
import { T as TooltipBehavior } from './behavior-Co00uT-D.js';
import { T as TooltipFC } from './component-BXCnkVUN.js';
import { S as SpanFC, b as buildBadgeTextString, s as showExpertSlot } from './component-DepnuZGT.js';
import { c as clsx } from './clsx-COFh-Vc8.js';
import { a as createRelatedUniqueId } from './dev.utils-Cib2ENyx.js';
import { f as KolPopoverButtonWcTag } from './component-names-DwvrfFak.js';
import { K as KolAlertFc } from './Alert-BjiZcCeA.js';

const validateAdjustHeight = (component, value) => {
    watchBoolean(component, '_adjustHeight', value);
};

const validateHideMsg = (component, value, options) => {
    watchBoolean(component, '_hideMsg', value, options);
};

const validateHint = (component, value) => {
    watchString(component, '_hint', value);
};

const validateMsg = (component, value) => {
    objectObjectHandler(value, () => {
        try {
            value = parseJson(value);
        }
        catch (_a) {
        }
        watchValidator(component, `_msg`, (value) => {
            if (value === undefined) {
                return true;
            }
            if (typeof value === 'string' && value.length > 0) {
                return true;
            }
            if (isObject(value) && value !== null) {
                const desc = value._description;
                return isString(desc, 1);
            }
            return false;
        }, new Set(['MsgPropType', 'string']), value);
    });
};
function isMsgDefinedAndInputTouched(msg, touched) {
    return Boolean(msg) && touched === true;
}
function normalizeMsg(msg) {
    if (typeof msg === 'string') {
        try {
            return parseJson(msg);
        }
        catch (_a) {
            return { _description: msg, _type: 'error' };
        }
    }
    if (msg && typeof msg === 'object' && !('_type' in msg)) {
        return Object.assign(Object.assign({}, msg), { _type: 'error' });
    }
    return msg;
}
function getMsgType(msg) {
    var _a;
    if (typeof msg === 'string') {
        return 'error';
    }
    return (_a = msg === null || msg === void 0 ? void 0 : msg._type) !== null && _a !== void 0 ? _a : 'error';
}

const validateTouched = (component, value) => {
    watchBoolean(component, '_touched', value);
};

class ControlledInputController extends AssociatedInputController {
    constructor(component, name, host) {
        super(component, name, host);
        this.component = component;
    }
    validateTouched(value) {
        validateTouched(this.component, value);
    }
    componentWillLoad() {
        super.componentWillLoad();
        this.validateTouched(this.component._touched);
    }
}

class InputController extends ControlledInputController {
    constructor(component, name, host) {
        super(component, name, host);
        this.valueChangeListeners = [];
        this.inputHasFocus = false;
        this.onFacade = {
            onBlur: this.onBlur.bind(this),
            onChange: this.onChange.bind(this),
            onClick: this.onClick.bind(this),
            onFocus: this.onFocus.bind(this),
            onInput: this.onInput.bind(this),
            onKeyDown: this.onKeyDown.bind(this),
        };
        this.component = component;
    }
    validateAccessKey(value) {
        validateAccessKey(this.component, value);
        validateAccessAndShortKey(value, this.component._shortKey);
    }
    validateAdjustHeight(value) {
        validateAdjustHeight(this.component, value);
    }
    validateDisabled(value) {
        validateDisabled(this.component, value);
    }
    validateTooltipAlign(value) {
        validateTooltipAlign(this.component, value);
    }
    validateHideMsg(value) {
        validateHideMsg(this.component, value, {
            hooks: {
                afterPatch: () => {
                    if (this.component.state._hideMsg) {
                        a11yHint('Property _hideMsg for inputs: Only use when the error message is shown outside of the input component.');
                    }
                },
            },
        });
    }
    validateHideLabel(value) {
        validateHideLabel(this.component, value, {
            hooks: {
                afterPatch: () => {
                    if (this.component.state._hideLabel) {
                        a11yHint('Property hide-label for inputs: Only use for exceptions like search inputs that are clearly identifiable by their context.');
                    }
                },
            },
        });
    }
    validateHint(value) {
        validateHint(this.component, value);
    }
    validateLabel(value) {
        validateLabelWithExpertSlot(this.component, value, {
            required: true,
        });
    }
    validateMsg(value) {
        validateMsg(this.component, value);
    }
    validateOn(value) {
        if (typeof value === 'object') {
            setState(this.component, '_on', value);
        }
    }
    validateShortKey(value) {
        validateShortKey(this.component, value);
        validateAccessAndShortKey(this.component._accessKey, value);
    }
    validateSmartButton(value) {
        objectObjectHandler(value, () => {
            try {
                value = parseJson(value);
            }
            catch (_a) {
            }
            setState(this.component, '_smartButton', value);
        });
    }
    validateTabIndex(value) {
        validateTabIndex(this.component, value);
    }
    validateVariant(value) {
        validateVariantClassName(this.component, value);
    }
    componentWillLoad() {
        super.componentWillLoad();
        this.validateAccessKey(this.component._accessKey);
        this.validateAdjustHeight(this.component._adjustHeight);
        this.validateMsg(this.component._msg);
        this.validateDisabled(this.component._disabled);
        this.validateHideMsg(this.component._hideMsg);
        this.validateHideLabel(this.component._hideLabel);
        this.validateHint(this.component._hint);
        this.validateLabel(this.component._label);
        this.validateShortKey(this.component._shortKey);
        this.validateSmartButton(this.component._smartButton);
        this.validateOn(this.component._on);
        this.validateTabIndex(this.component._tabIndex);
        this.validateVariant(this.component._variant);
        validateAccessAndShortKey(this.component._accessKey, this.component._shortKey);
    }
    emitEvent(type, value) {
        if (this.host) {
            dispatchDomEvent(this.host, type, value);
        }
    }
    onChange(event, value) {
        var _a;
        event.stopPropagation();
        if (typeof value === 'undefined') {
            value = event.target.value;
        }
        this.emitEvent(KolEvent.change, value);
        if (typeof ((_a = this.component._on) === null || _a === void 0 ? void 0 : _a.onChange) === 'function') {
            this.component._on.onChange(event, value);
        }
        this.valueChangeListeners.forEach((listener) => listener(value));
    }
    onInput(event, shouldSetFormAssociatedValue = true, value) {
        var _a;
        event.stopPropagation();
        if (typeof value === 'undefined') {
            value = event.target.value;
        }
        this.emitEvent(KolEvent.input, value);
        if (shouldSetFormAssociatedValue) {
            this.setFormAssociatedValue(value);
        }
        if (typeof ((_a = this.component._on) === null || _a === void 0 ? void 0 : _a.onInput) === 'function') {
            this.component._on.onInput(event, value);
        }
    }
    onClick(event) {
        var _a;
        this.emitEvent(KolEvent.click);
        if (typeof ((_a = this.component._on) === null || _a === void 0 ? void 0 : _a.onClick) === 'function') {
            this.component._on.onClick(event);
        }
    }
    onFocus(event) {
        var _a;
        if (!this.inputHasFocus) {
            this.emitEvent(KolEvent.focus);
            if (typeof ((_a = this.component._on) === null || _a === void 0 ? void 0 : _a.onFocus) === 'function') {
                this.component._on.onFocus(event);
            }
            this.inputHasFocus = true;
        }
    }
    onBlur(event) {
        var _a, _b;
        if (this.component._disabled) {
            return;
        }
        const root = ((_a = this.host) === null || _a === void 0 ? void 0 : _a.shadowRoot) || this.host;
        const isFocusInside = (root === null || root === void 0 ? void 0 : root.contains(event.relatedTarget)) || this.host === event.relatedTarget;
        if (this.inputHasFocus && !isFocusInside) {
            this.component._touched = true;
            this.emitEvent(KolEvent.blur);
            if (typeof ((_b = this.component._on) === null || _b === void 0 ? void 0 : _b.onBlur) === 'function') {
                this.component._on.onBlur(event);
            }
            this.inputHasFocus = false;
        }
    }
    onKeyDown(event) {
        var _a;
        this.emitEvent(KolEvent.keydown);
        if (typeof ((_a = this.component._on) === null || _a === void 0 ? void 0 : _a.onKeyDown) === 'function') {
            this.component._on.onKeyDown(event);
        }
    }
    addValueChangeListener(listener) {
        this.valueChangeListeners.push(listener);
    }
    hasSoftCharacterLimit() {
        return typeof this.component.state._maxLength === 'number' && this.component.state._maxLengthBehavior === 'soft';
    }
    hasCounter() {
        return this.component.state._hasCounter === true;
    }
}

function getDefaultProps({ ariaDescribedBy, hideLabel, label }) {
    return {
        title: '',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        'aria-describedby': (ariaDescribedBy === null || ariaDescribedBy === void 0 ? void 0 : ariaDescribedBy.length) ? ariaDescribedBy.join(' ') : undefined,
        'aria-label': hideLabel && label ? label : undefined,
    };
}

const getRenderStates = (state) => {
    const msg = state._msg;
    const description = typeof msg === 'string' ? msg : msg === null || msg === void 0 ? void 0 : msg._description;
    const type = getMsgType(msg);
    const hasMessage = Boolean(description && description.length > 0);
    const isMessageValidError = type === 'error' && hasMessage;
    const hasError = isMessageValidError && state._touched === true;
    const hasHint = typeof state._hint === 'string' && state._hint.length > 0;
    const ariaDescribedBy = [];
    if (hasMessage && !state._hideMsg) {
        ariaDescribedBy.push(createRelatedUniqueId(state._id, 'msg'));
    }
    if (hasHint === true) {
        ariaDescribedBy.push(createRelatedUniqueId(state._id, 'hint'));
    }
    if (hasError === true) {
        ariaDescribedBy.push(createRelatedUniqueId(state._id, 'error'));
    }
    return { hasError, hasHint, ariaDescribedBy };
};

const KolFormFieldHintFc = (_a) => {
    var { id, class: classNames, hint, baseClassName = 'kol-form-field' } = _a, other = __rest(_a, ["id", "class", "hint", "baseClassName"]);
    if (!hint) {
        return null;
    }
    return (h("span", Object.assign({ class: clsx(`${baseClassName}__hint`, classNames), id: createRelatedUniqueId(id || '', 'hint') }, other), hint));
};

const KolFormFieldLabelFc = (_a) => {
    var { component: Component = 'label', id, baseClassName = 'kol-form-field', class: classNames, accessKey, shortKey, label, hideLabel, hasExpertSlot, showBadge = true, readOnly, infoPopover } = _a, other = __rest(_a, ["component", "id", "baseClassName", "class", "accessKey", "shortKey", "label", "hideLabel", "hasExpertSlot", "showBadge", "readOnly", "infoPopover"]);
    const useTooltipInsteadOfLabel = !hasExpertSlot && hideLabel;
    const translateReadOnly = translate('kol-readonly');
    const badgeText = showBadge === false ? undefined : buildBadgeTextString(accessKey, shortKey);
    return (h(Component, Object.assign({}, other, { class: clsx(`${baseClassName}__label`, classNames), id: !useTooltipInsteadOfLabel ? createRelatedUniqueId(id, 'label') : undefined, hidden: useTooltipInsteadOfLabel, htmlFor: id }), h(SpanFC, { class: `${baseClassName}__label-text`, label: hasExpertSlot ? '' : (label !== null && label !== void 0 ? label : ''), badgeText: badgeText }, h("slot", { name: "expert" })), !hasExpertSlot && readOnly && (h("span", { class: `${baseClassName}__label__read-only`, "aria-hidden": "true" }, "(", translateReadOnly, ")")), !hasExpertSlot && infoPopover && (h(KolPopoverButtonWcTag, Object.assign({ _variant: "ghost" }, infoPopover, { _hideLabel: true, _inline: true }), h("div", { class: "kol-popover-button__popover--styled" }, infoPopover._content)))));
};

const FormFieldMsgFc = (_a) => {
    var _b, _c;
    var { alert, msg, id, class: classNames } = _a, other = __rest(_a, ["alert", "msg", "id", "class"]);
    const message = normalizeMsg(msg);
    return (h(KolAlertFc, Object.assign({ id: createRelatedUniqueId(id, 'msg'), alert: (_b = message === null || message === void 0 ? void 0 : message._alert) !== null && _b !== void 0 ? _b : alert, hasCloser: false, level: 0, type: (_c = message === null || message === void 0 ? void 0 : message._type) !== null && _c !== void 0 ? _c : 'error', variant: "msg", class: clsx('kol-form-field__msg', classNames) }, other), (message === null || message === void 0 ? void 0 : message._description) || undefined));
};

const formFieldTooltipBehaviorPool = new Map();
const getFormFieldTooltipBehavior = (id) => {
    const tooltipBehavior = formFieldTooltipBehaviorPool.get(id);
    if (tooltipBehavior) {
        return tooltipBehavior;
    }
    const nextTooltipBehavior = new TooltipBehavior(BaseWebComponent.stateLess);
    nextTooltipBehavior.componentWillLoad({ label: '' });
    formFieldTooltipBehaviorPool.set(id, nextTooltipBehavior);
    return nextTooltipBehavior;
};
const destroyFormFieldTooltipBehavior = (id) => {
    const tooltipBehavior = formFieldTooltipBehaviorPool.get(id);
    if (tooltipBehavior) {
        tooltipBehavior.destroy();
        formFieldTooltipBehaviorPool.delete(id);
    }
};
function getModifierClassNameByMsgType(msg) {
    if (msg === null || msg === void 0 ? void 0 : msg.type) {
        return ({
            default: 'msg-type-default',
            info: 'msg-type-info',
            success: 'msg-type-success',
            warning: 'msg-type-warning',
            error: 'msg-type-error',
        }[msg === null || msg === void 0 ? void 0 : msg.type] || '');
    }
    return '';
}
const InputContainer = (_a, children) => {
    var { class: classNames } = _a, other = __rest(_a, ["class"]);
    return (h("div", Object.assign({ class: clsx('kol-form-field__input', classNames) }, other), children));
};
const KolFormFieldFc = (props, children) => {
    const { component: Component = 'div', renderNoLabel, renderNoTooltip, renderNoHint, anotherChildren, id, required, alert, disabled, class: classNames, msg, hideMsg, hideLabel, label, infoPopover, hint, accessKey, shortKey, counter, readOnly, touched, maxLength, ariaDescribedBy, showBadge, tooltipAlign, tooltipFloatingRef, variant, formFieldLabelProps, formFieldHintProps, formFieldTooltipProps, formFieldMsgProps, formFieldInputProps } = props, other = __rest(props, ["component", "renderNoLabel", "renderNoTooltip", "renderNoHint", "anotherChildren", "id", "required", "alert", "disabled", "class", "msg", "hideMsg", "hideLabel", "label", "infoPopover", "hint", "accessKey", "shortKey", "counter", "readOnly", "touched", "maxLength", "ariaDescribedBy", "showBadge", "tooltipAlign", "tooltipFloatingRef", "variant", "formFieldLabelProps", "formFieldHintProps", "formFieldTooltipProps", "formFieldMsgProps", "formFieldInputProps"]);
    const showLabel = !renderNoLabel;
    const showHint = !renderNoHint;
    const showTooltip = !renderNoTooltip;
    const hasExpertSlot = showExpertSlot(label);
    const showMsg = isMsgDefinedAndInputTouched(msg, touched);
    const badgeText = buildBadgeTextString(accessKey, shortKey);
    const useTooltipInsteadOfLabel = showTooltip && !hasExpertSlot && hideLabel;
    const labelId = createRelatedUniqueId(id, 'label');
    const tooltipBehavior = useTooltipInsteadOfLabel ? getFormFieldTooltipBehavior(id) : undefined;
    if (tooltipBehavior) {
        tooltipBehavior.watchAlign(tooltipAlign);
        tooltipBehavior.watchBadgeText(badgeText || '');
        tooltipBehavior.watchId(labelId);
        tooltipBehavior.watchLabel(label);
    }
    else {
        destroyFormFieldTooltipBehavior(id);
    }
    const forwardedInputRef = formFieldInputProps === null || formFieldInputProps === void 0 ? void 0 : formFieldInputProps.ref;
    const setInputContainerRef = (el) => {
        forwardedInputRef === null || forwardedInputRef === void 0 ? void 0 : forwardedInputRef(el);
        if (tooltipBehavior && el) {
            tooltipBehavior.initContext(el);
            tooltipBehavior.syncListeners(undefined, el, true);
        }
    };
    let stateCssClasses = {
        ['kol-form-field--disabled']: Boolean(disabled),
        ['kol-form-field--required']: Boolean(required),
        ['kol-form-field--touched']: Boolean(touched),
        ['kol-form-field--hide-label']: Boolean(hideLabel),
        ['kol-form-field--read-only']: Boolean(readOnly),
        ['kol-form-field--hidden-msg']: Boolean(hideMsg),
    };
    if (variant) {
        stateCssClasses = Object.assign(Object.assign({}, stateCssClasses), { [classNameFromVariant(variant, 'form-field')]: true });
    }
    if (showMsg) {
        const msgType = getMsgType(msg);
        stateCssClasses = Object.assign(Object.assign({}, stateCssClasses), { [`kol-form-field--${msgType}`]: true, [`kol-form-field--${getModifierClassNameByMsgType({ type: msgType })}`]: true });
    }
    return (h(Component, Object.assign({ class: clsx('kol-form-field', stateCssClasses, classNames), "aria-describedby": ariaDescribedBy }, other), showLabel && (h(KolFormFieldLabelFc, Object.assign({}, (formFieldLabelProps || {}), { id: id, hasExpertSlot: hasExpertSlot, hideLabel: hideLabel, label: label, accessKey: accessKey, shortKey: shortKey, readOnly: readOnly, showBadge: showBadge, infoPopover: infoPopover }))), h(InputContainer, Object.assign({}, formFieldInputProps, { ref: setInputContainerRef }), children, useTooltipInsteadOfLabel && hideLabel === true && (h("div", { class: clsx('kol-form-field__tooltip', formFieldTooltipProps === null || formFieldTooltipProps === void 0 ? void 0 : formFieldTooltipProps.class) }, h(TooltipFC, { badgeText: badgeText || '', label: label, align: tooltipAlign, id: labelId, refFloating: tooltipFloatingRef !== null && tooltipFloatingRef !== void 0 ? tooltipFloatingRef : ((el) => {
            tooltipBehavior === null || tooltipBehavior === void 0 ? void 0 : tooltipBehavior.setTooltipElementRef(el);
        }) })))), counter && !(counter.maxLengthBehavior === 'soft' && typeof counter.maxLength !== 'number') && (h("div", { class: "kol-form-field__counter" }, h("span", { "data-testid": "input-counter", "aria-hidden": "true", class: "kol-form-field__counter", ref: counter.visualRef }), h("span", { "aria-live": "polite", class: "visually-hidden", "data-testid": "input-counter-aria", id: createRelatedUniqueId(id, 'counter'), ref: counter.ariaRef }))), showMsg && !hideMsg && h(FormFieldMsgFc, Object.assign({}, (formFieldMsgProps || {}), { id: id, alert: alert, msg: msg })), showHint && h(KolFormFieldHintFc, Object.assign({}, (formFieldHintProps || {}), { id: id, hint: hint })), anotherChildren, typeof maxLength === 'number' && !counter && (h("span", { id: createRelatedUniqueId(id, 'character-limit-hint'), class: "visually-hidden" }, translate('kol-character-limit-hint', { placeholders: { limit: String(maxLength) } })))));
};

function getFormFieldProps(state, counterRefs) {
    const props = {
        id: state._id,
        disabled: state._disabled,
        msg: state._msg,
        hint: state._hint,
        label: state._label,
        hideLabel: state._hideLabel,
        hideMsg: state._hideMsg,
        touched: state._touched,
        showBadge: ('_accessKey' in state && Boolean(state._accessKey)) || ('_shortKey' in state && Boolean(state._shortKey)),
    };
    if ('_required' in state) {
        props.required = state._required;
    }
    if ('_readOnly' in state) {
        props.readOnly = state._readOnly;
    }
    if ('_accessKey' in state) {
        props.accessKey = state._accessKey;
    }
    if ('_shortKey' in state) {
        props.shortKey = state._shortKey;
    }
    if ('_maxLength' in state) {
        props.maxLength = state._maxLength;
    }
    if ('_hasCounter' in state && state._hasCounter === true) {
        props.counter = Object.assign({ maxLength: '_maxLength' in state ? state._maxLength : undefined, maxLengthBehavior: ('_maxLengthBehavior' in state ? state._maxLengthBehavior : undefined) || 'hard' }, counterRefs);
    }
    if ('_variant' in state) {
        props.variant = state._variant;
    }
    return props;
}
const FormFieldStateWrapper = (_a, children) => {
    var { state, counterRefs } = _a, other = __rest(_a, ["state", "counterRefs"]);
    const { ariaDescribedBy: ariaDescribedByArray } = getRenderStates(state);
    const isRadioVariant = other.component === 'fieldset' || ('_options' in state && '_orientation' in state);
    const ariaDescribedBy = isRadioVariant && ariaDescribedByArray.length > 0 ? ariaDescribedByArray.join(' ') : undefined;
    const baseProps = getFormFieldProps(state, counterRefs);
    return (h(KolFormFieldFc, Object.assign({}, baseProps, other, { ariaDescribedBy: ariaDescribedBy }), children));
};

export { FormFieldStateWrapper as F, InputController as I, KolFormFieldLabelFc as K, getRenderStates as a, KolFormFieldHintFc as b, getDefaultProps as c, getMsgType as g, isMsgDefinedAndInputTouched as i };
//# sourceMappingURL=FormFieldStateWrapper-CANKwnKv.js.map

//# sourceMappingURL=FormFieldStateWrapper-CANKwnKv.js.map